import React, { useState, useRef, useEffect } from "react";
import { apiService } from "../../../services/apiService";
import "./AddEmulator.css";
import { Button } from "../../components/Utilities";
import pro from "../../assets/person.svg";
import camera from "../../assets/photo.svg";
import share from "../../assets/share.svg";
import { Loading } from "../../components/Loading"; // Import Loading component

export const AddDriver = ({
  isOpen,
  onClose,
  onDriverAdded,
  driverType = "evaluator",
}) => {
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [licenseFile, setLicenseFile] = useState(null);
  const [licensePreview, setLicensePreview] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Camera states
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Attach stream to <video>
  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current
        .play()
        .catch((err) => console.error("Video play blocked:", err));
    }
  }, [showCamera, stream]);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  if (!isOpen) return null;

  const handleLicenseUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLicenseFile(file);
      setLicensePreview(URL.createObjectURL(file));
    }
  };

  const handleProfilePictureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfileFile(file);
      setProfilePicture(URL.createObjectURL(file));
    }
  };

  const handleOpenCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      setStream(mediaStream);
      setShowCamera(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError("Unable to access camera. Please allow permissions.");
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      const file = new File([blob], "captured-photo.jpg", {
        type: "image/jpeg",
      });
      setProfileFile(file);
      setProfilePicture(URL.createObjectURL(file));
    });

    stopCamera();
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setShowCamera(false);
    setStream(null);
  };

  const handleAddDriver = async () => {
    if (!name || !phoneNumber || !licenseFile || !profileFile) {
      setError(
        "Please fill in all required fields and upload both license and profile picture."
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Upload license file
      const licenseBase64 = await fileToBase64(licenseFile);
      const licenseBase64String = licenseBase64.replace(
        /^data:[^;]+;base64,/,
        ""
      );
      const licenseFileName = `driver_license_${name}_${Date.now()}.${licenseFile.name
        .split(".")
        .pop()}`;
      const licensePayload = {
        file: licenseBase64String,
        folder: "drivers",
        fileName: licenseFileName,
        metadata: {
          contentType: licenseFile.type,
          originalName: licenseFile.name,
          size: licenseFile.size,
        },
      };
      const licenseResponse = await apiService.uploadFile(licensePayload);
      const licenseUrl = licenseResponse.data?.publicUrl || "";

      // Upload profile image
      const profileBase64 = await fileToBase64(profileFile);
      const profileBase64String = profileBase64.replace(
        /^data:[^;]+;base64,/,
        ""
      );
      const profileFileName = `driver_profile_${name}_${Date.now()}.${profileFile.name
        .split(".")
        .pop()}`;
      const profilePayload = {
        file: profileBase64String,
        folder: "drivers",
        fileName: profileFileName,
        metadata: {
          contentType: profileFile.type,
          originalName: profileFile.name,
          size: profileFile.size,
        },
      };
      const profileResponse = await apiService.uploadFile(profilePayload);
      const imageUrl = profileResponse.data?.publicUrl || "";

      const driverData = {
        name,
        phone: phoneNumber,
        licenseUrl,
        imageUrl,
        type: driverType,
        // type: driverType,
        // type: driverType 
        // type: driverType,  // Commented by Snehal to resolve builderror
      };

      console.log("Sending driver data to API:", driverData);

      await apiService.createDriver(driverData);
      console.log("Driver created successfully:", driverData);
      onDriverAdded();
      setName("");
      setPhoneNumber("");
      setLicenseFile(null);
      setLicensePreview(null);
      setProfileFile(null);
      setProfilePicture(null);
      onClose();
    } catch (err) {
      console.error("Error creating driver:", err);
      if (err.code === "ECONNABORTED") {
        setError(
          "Request timed out. Please check your network or try again later."
        );
      } else if (err.response?.status === 400) {
        setError("Invalid data provided. Please check your inputs.");
      } else {
        setError(
          `Failed to add driver. Please try again. Details: ${
            err.message || "Unknown error"
          }`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Handler to open image modal
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  // Handler to close image modal
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // Check if all required fields are filled
  const isAddDriverDisabled = !name || !phoneNumber || !licenseFile || !profileFile || loading;

  return (
    <div className="add-driver">
      <div className="modal-overlay">
      <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="model-header">
            <p>Add a Driver</p>
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="model-body">
            {loading && <Loading />} {/* Add Loading component */}
            {showCamera && (
              <div className="camera-overlay">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="camera-video"
                ></video>
                <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
                <div className="camera-controls">
                  <Button className="add-evaluator-btn" onClick={handleCapture}>
                    Capture
                  </Button>
                  <Button className="upload-btn-modal" onClick={stopCamera}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div className="right-part">
              <div className="evaluator-photo">
                <div className="profile-pic">
                  {profilePicture == null ? (
                    <img src={pro} alt="" width="50%" />
                  ) : (
                    <img
                      src={profilePicture}
                      alt=""
                      width="50%"
                      className="uploadModel-img"
                      onClick={() => handleImageClick(profilePicture)}
                    />
                  )}
                </div>
                <div className="evaluator-section-part">
                  {/* <Button className="model-btn" onClick={handleOpenCamera}>
                    <img src={camera} alt="" className="camera" /> Take Photo
                  </Button> */}

                  <input
                    type="file"
                    id="profilePictureInput"
                    accept="image/heic,image/heif,image/jpeg,image/jpg,image/png,image/gif,image/tiff,image/webp,image/bmp,image/x-adobe-dng,image/x-dng,application/pdf,video/mp4,video/quicktime,video/x-matroska,video/webm,video/3gpp,video/x-msvideo,video/hevc,video/h265"
                    // accept="image/jpeg,image/jpg,image/png,application/pdf"
                    onChange={handleProfilePictureUpload}
                    style={{ display: "none" }}
                  />
                  <Button
                    className="model-btn"
                    onClick={() =>
                      document.getElementById("profilePictureInput").click()
                    }
                  >
                    <img src={share} className="share" alt="" /> Upload Photo
                  </Button>
                </div>
              </div>
              <div className="emulator-inputs">
                <label>Driver’s Name*</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                      const value = e.target.value;
                      // Allow only letters (a-z, A-Z) and spaces; limit to 50 characters
                      if (/^[a-zA-Z\s]*$/.test(value) && value.length <= 50) {
                        setName(value);
                      }
                    }}
                    pattern="[a-zA-Z\s]{2,50}"
                    title="Name must contain only letters and spaces, and be between 2 and 50 characters."
                    placeholder="Enter your name"
                    required
                />
                <label>Driver’s Phone Number*</label>
                <input
                  type="tel"
                  inputMode="numeric" // better mobile keyboard
                  value={phoneNumber}
                  onChange={(e) => {
                      const value = e.target.value;
                      // Allow only digits and limit to 10 characters
                      if (/^\d*$/.test(value) && value.length <= 10) {
                        setPhoneNumber(value);
                      }
                    }}
                    pattern="[0-9]{10}"
                    placeholder="Enter your Phone Number"
                    required
                />
              </div>
            </div>

            <div className="left-part">
              <div className="license-upload-part">
                <div className="license-preview">
                  {licensePreview == null ? (
                    <img src={pro} alt="" width="50%" />
                  ) : (
                    <img
                      src={licensePreview}
                      alt=""
                      width="50%"
                      className="uploadModel-img"
                      onClick={() => handleImageClick(licensePreview)}
                    />
                  )}
                </div>
                <input
                  type="file"
                  id="licenseFileInput"
                  accept="image/heic,image/heif,image/jpeg,image/jpg,image/png,image/gif,image/tiff,image/webp,image/bmp,image/x-adobe-dng,image/x-dng,application/pdf,video/mp4,video/quicktime,video/x-matroska,video/webm,video/3gpp,video/x-msvideo,video/hevc,video/h265"

                  // accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handleLicenseUpload}
                  style={{ display: "none" }}
                />
                <Button
                  className="upload-btn-modal"
                  onClick={() =>
                    document.getElementById("licenseFileInput").click()
                  }
                >
                  <img src={share} className="share" alt="" /> Upload Driver’s
                  License*
                </Button>
              </div>
              {error && <p className="error">{error}</p>}
              {error && <p className="error">{error}</p>}
              <Button
                className="add-evaluator-btn"
                onClick={handleAddDriver}
                disabled={isAddDriverDisabled} // Updated to check all fields
              >
                {loading ? "Adding..." : "Add Driver"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={handleCloseImageModal}>
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="image-modal-close"
              onClick={handleCloseImageModal}
            >
              ×
            </button>
            <img
              src={selectedImage}
              alt="Full-size Preview"
              className="full-size-image"
            />
          </div>
        </div>
      )}
    </div>
    </div>
    
  );
};
